import { useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

/**
 * Custom hook that provides router functionality
 *
 * This is a replacement for the connected-react-router push/replace functions
 * and for the useHistory hook from react-router v5
 */
export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  return useMemo(() => {
    return {
      // Navigation methods
      push: (
        path: string | { pathname: string; search?: string; hash?: string }
      ) => {
        if (typeof path === 'string') {
          navigate(path);
        } else {
          const url = path.pathname + (path.search || '') + (path.hash || '');
          navigate(url);
        }
      },
      replace: (
        path:
          | string
          | { pathname: string; search?: string; hash?: string; state?: any }
      ) => {
        if (typeof path === 'string') {
          navigate(path, { state: 'active', replace: true });
        } else {
          const url = path.pathname + (path.search || '') + (path.hash || '');
          navigate(url, { state: path.state, replace: true });
        }
      },
      goBack: () => navigate(-1),

      // Location properties
      location,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,

      // Params
      params,

      // Block navigation (not directly supported in v6, would need custom implementation)
      block: () => {
        console.warn(
          'Navigation blocking is not directly supported in React Router v7. Consider using a custom prompt component.'
        );
        return () => {}; // Return no-op cleanup function
      },
    };
  }, [navigate, location, params]);
}
